import type { GoodsReceipt, PurchaseOrder } from "@station/contracts";
import { Alert, Button, MoneyText, SkeletonRows, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Ban, Lock, PackageCheck, Pencil, Send, Truck, Undo2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestPanel } from "../../../../features/approvals/request-panel.js";
import { ReasonDialog } from "../../../../features/counterparties/shared.js";
import { Section } from "../../../../features/expenses/shared.js";
import { Th } from "../../../../features/fuel/shared.js";
import { formatStockQuantity } from "../../../../features/market/market-values.js";
import { OrderDialog } from "../../../../features/market/order-form.js";
import { useGoodsReceipts, usePurchaseOrder } from "../../../../features/market/purchasing-queries.js";
import { purchaseOrderActions } from "../../../../features/market/purchasing-values.js";
import { useMarketPermissions, useRefreshMarket } from "../../../../features/market/queries.js";
import { ReceiptDialog } from "../../../../features/market/receipt-dialog.js";
import {
  crumbLink,
  Field,
  GoodsReceiptStatusBadge,
  MarketPage,
  PurchaseOrderStatusBadge,
  ReceiptCostBadge,
  TableFrame,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/market/siparisler/$id")({ component: PurchaseOrderRoute });

function PurchaseOrderRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const order = usePurchaseOrder(id);
  const title = order.data?.orderNo ?? t("market.orders.detailTitle");
  return (
    <MarketPage
      title={title}
      breadcrumb={[crumbLink(t("menu.marketPurchaseOrders"), "/market/siparisler"), { label: title }]}
    >
      {() =>
        order.error ? (
          <Alert tone="danger">{errorMessage(order.error)}</Alert>
        ) : !order.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <PurchaseOrderDetail order={order.data} />
        )
      }
    </MarketPage>
  );
}

type ReasonAction = "close" | "cancel";

function PurchaseOrderDetail({ order }: { order: PurchaseOrder }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const stations = useMarketStations(order.companyId);
  const [editing, setEditing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const actions = purchaseOrderActions(order);
  const canManage = permissions.canManagePurchases;
  const showSuggested = order.lines.some((line) => line.suggestedQuantity !== null);

  const act = useMutation({
    mutationFn: async (action: "submit" | "send" | "reopen") => {
      const params = { path: { id: order.id } };
      const body = { version: order.version };
      if (action === "submit")
        return (await api.POST("/market/purchase-orders/{id}/submit", { params, body })).data!;
      if (action === "send")
        return (await api.POST("/market/purchase-orders/{id}/send", { params, body })).data!;
      return (await api.POST("/market/purchase-orders/{id}/reopen", { params, body })).data!;
    },
    onSuccess: async (saved, action) => {
      await refresh();
      // Onaya gönderimde politika yoksa sipariş hemen ONAYLANIR: kullanıcıya hangisinin olduğu söylenir.
      const key = action === "submit" ? (saved.status === "APPROVED" ? "approved" : "submitted") : action;
      toast({ tone: "success", title: t(`market.orders.done.${key}`) });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  const withReason = async (action: ReasonAction, reason: string) => {
    const params = { path: { id: order.id } };
    const body = { version: order.version, reason };
    if (action === "close") await api.POST("/market/purchase-orders/{id}/close", { params, body });
    else await api.POST("/market/purchase-orders/{id}/cancel", { params, body });
    await refresh();
    toast({ tone: "success", title: t(`market.orders.done.${action}`) });
  };

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-lg font-semibold">{order.orderNo}</span>
        <PurchaseOrderStatusBadge status={order.status} />
        <span className="text-sm text-muted-foreground">
          {order.supplierName} · {order.stationName}
        </span>
        <div className="ms-auto flex flex-wrap gap-2">
          {canManage && actions.edit ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil aria-hidden="true" />
              {t("common.edit")}
            </Button>
          ) : null}
          {canManage && actions.submit ? (
            <Button
              loading={act.isPending && act.variables === "submit"}
              onClick={() => act.mutate("submit")}
            >
              <Send aria-hidden="true" />
              {t("market.orders.submit")}
            </Button>
          ) : null}
          {canManage && actions.send ? (
            <Button loading={act.isPending && act.variables === "send"} onClick={() => act.mutate("send")}>
              <Truck aria-hidden="true" />
              {t("market.orders.send")}
            </Button>
          ) : null}
          {permissions.canManageReceipts && actions.receive ? (
            <Button onClick={() => setReceiving(true)}>
              <PackageCheck aria-hidden="true" />
              {t("market.orders.startReceipt")}
            </Button>
          ) : null}
          {canManage && actions.reopen ? (
            <Button
              variant="outline"
              loading={act.isPending && act.variables === "reopen"}
              onClick={() => act.mutate("reopen")}
            >
              <Undo2 aria-hidden="true" />
              {t("market.orders.reopen")}
            </Button>
          ) : null}
          {canManage && actions.close ? (
            <Button variant="outline" onClick={() => setReasonAction("close")}>
              <Lock aria-hidden="true" />
              {t("market.orders.close")}
            </Button>
          ) : null}
          {canManage && actions.cancel ? (
            <Button variant="destructive" onClick={() => setReasonAction("cancel")}>
              <Ban aria-hidden="true" />
              {t("market.orders.cancel")}
            </Button>
          ) : null}
        </div>
      </div>

      {order.status === "PENDING_APPROVAL" ? (
        <Alert tone="info">{t("market.orders.pendingHint")}</Alert>
      ) : null}
      {order.status === "REJECTED" ? <Alert tone="warning">{t("market.orders.rejectedHint")}</Alert> : null}
      {order.status === "APPROVED" ? <Alert tone="info">{t("market.orders.approvedHint")}</Alert> : null}
      {order.closedReason ? (
        <Alert
          tone={order.status === "CANCELLED" ? "warning" : "info"}
          title={t(
            order.status === "CANCELLED" ? "market.orders.cancelledTitle" : "market.orders.closedTitle",
          )}
        >
          {order.closedReason}
        </Alert>
      ) : null}

      <Section title={t("market.orders.summary")}>
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("market.orders.fields.supplier")}>{order.supplierName}</Field>
          <Field label={t("market.fields.station")}>{order.stationName}</Field>
          <Field label={t("market.orders.fields.orderDate")}>{formatDate(order.orderDate)}</Field>
          <Field label={t("market.orders.fields.expectedDate")}>{formatDate(order.expectedDate)}</Field>
          <Field label={t("market.orders.fields.netAmount")}>
            <MoneyText value={order.netAmount} />
          </Field>
          <Field label={t("market.orders.fields.sentAt")}>{formatDateTime(order.sentAt)}</Field>
          <Field label={t("market.fields.createdAt")}>{formatDateTime(order.createdAt)}</Field>
        </dl>
        {order.note ? <p className="text-sm">{order.note}</p> : null}
      </Section>

      <Section title={t("market.orders.lines")}>
        <TableFrame label={t("market.orders.lines")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("market.fields.product")}</Th>
              {showSuggested ? <Th align="right">{t("market.suggestions.fields.suggested")}</Th> : null}
              <Th align="right">{t("market.orders.fields.quantity")}</Th>
              <Th align="right">{t("market.orders.fields.unitPrice")}</Th>
              <Th align="right">{t("market.orders.fields.lineAmount")}</Th>
              <Th align="right">{t("market.orders.fields.received")}</Th>
              <Th align="right">{t("market.orders.fields.remaining")}</Th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((line) => (
              <tr key={line.id} className="border-t">
                <td className="px-3 py-2">
                  <span className="grid gap-0.5">
                    <span>{line.productName}</span>
                    <span className="font-mono text-xs text-muted-foreground">{line.productCode}</span>
                  </span>
                </td>
                {showSuggested ? (
                  <td className="px-3 py-2 text-right tabular text-muted-foreground">
                    {formatStockQuantity(line.suggestedQuantity)}
                  </td>
                ) : null}
                <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                  {formatStockQuantity(line.quantity, line.unit)}
                </td>
                <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                  {formatUnitPrice(line.unitPrice)}
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={line.lineAmount} />
                </td>
                <td className="px-3 py-2 text-right tabular">{formatStockQuantity(line.receivedQuantity)}</td>
                <td className="px-3 py-2 text-right tabular">
                  {formatStockQuantity(line.remainingQuantity)}
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
        <p className="text-xs text-muted-foreground">{t("market.orders.vatHint")}</p>
      </Section>

      <OrderReceipts order={order} />

      {order.approvalRequestId ? (
        <Section title={t("market.orders.approval")}>
          <ApprovalRequestPanel requestId={order.approvalRequestId} showSummary={false} />
        </Section>
      ) : null}

      {canManage && actions.edit ? (
        <OrderDialog
          open={editing}
          onOpenChange={setEditing}
          companyId={order.companyId}
          stations={stations.stations}
          defaultStationId={order.stationId}
          order={order}
          onSaved={() => undefined}
        />
      ) : null}
      {permissions.canManageReceipts && actions.receive ? (
        <ReceiptDialog
          open={receiving}
          onOpenChange={setReceiving}
          companyId={order.companyId}
          stations={stations.stations}
          defaultStationId={order.stationId}
          order={order}
          onSaved={(saved) => void navigate({ to: "/market/mal-kabul/$id", params: { id: saved.id } })}
        />
      ) : null}
      <ReasonDialog
        open={reasonAction !== null}
        onOpenChange={(open) => !open && setReasonAction(null)}
        title={t(reasonAction === "close" ? "market.orders.closeTitle" : "market.orders.cancelTitle")}
        description={t(
          reasonAction === "close" ? "market.orders.closeDescription" : "market.orders.cancelDescription",
        )}
        confirmLabel={t(reasonAction === "close" ? "market.orders.close" : "market.orders.cancel")}
        onConfirm={(reason) => withReason(reasonAction ?? "cancel", reason)}
      />
    </div>
  );
}

/** Siparişten açılmış mal kabuller (sipariş ilerlemesi bunlardan türetilir). */
function OrderReceipts({ order }: { order: PurchaseOrder }): ReactElement | null {
  const { t } = useTranslation();
  const receipts = useGoodsReceipts(order.companyId, { purchaseOrderId: order.id }, undefined, {
    limit: 100,
  });
  const items: GoodsReceipt[] = receipts.data?.items ?? [];
  if (order.status === "DRAFT" || order.status === "PENDING_APPROVAL" || order.status === "REJECTED")
    return null;
  return (
    <Section title={t("market.orders.receipts")}>
      {receipts.error ? (
        <Alert tone="danger">{errorMessage(receipts.error)}</Alert>
      ) : receipts.isPending ? (
        <SkeletonRows rows={2} />
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("market.orders.noReceipts")}</p>
      ) : (
        <TableFrame label={t("market.orders.receipts")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>{t("market.receipts.fields.receiptNo")}</Th>
              <Th>{t("market.receipts.fields.despatchNo")}</Th>
              <Th>{t("common.status")}</Th>
              <Th>{t("market.receipts.fields.cost")}</Th>
              <Th align="right">{t("market.receipts.fields.received")}</Th>
              <Th align="right">{t("market.receipts.fields.netAmount")}</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((receipt) => (
              <tr key={receipt.id} className="border-t">
                <td className="px-3 py-2">
                  <Link
                    to="/market/mal-kabul/$id"
                    params={{ id: receipt.id }}
                    className="grid gap-0.5 underline underline-offset-4"
                  >
                    <span className="font-mono whitespace-nowrap">{receipt.receiptNo}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(receipt.businessDate)}</span>
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{receipt.despatchNo ?? "—"}</td>
                <td className="px-3 py-2">
                  <GoodsReceiptStatusBadge status={receipt.status} />
                </td>
                <td className="px-3 py-2">
                  <ReceiptCostBadge status={receipt.costStatus} />
                </td>
                <td className="px-3 py-2 text-right tabular">
                  {formatStockQuantity(receipt.totals.receivedQuantity)}
                </td>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={receipt.netAmount} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableFrame>
      )}
    </Section>
  );
}
